import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Receipt,
  CreditCard,
  TrendingUp,
  Clock,
  CheckCircle,
  ExternalLink,
  Loader2,
  AlertCircle,
  Gift,
  RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/errorHelper';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CommissionsDashboard() {
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);
  const [fetchingReceipt, setFetchingReceipt] = useState(null);
  const [reconciling, setReconciling] = useState(false);

  const handleReconcile = async () => {
    if (reconciling) return;
    setReconciling(true);
    try {
      const res = await axios.post(`${API}/restaurant-dashboard/commissions/reconcile`);
      const { created, skipped_existing, total_orders_scanned } = res.data || {};
      if (created > 0) {
        toast.success(`${created} commission(s) recuperee(s) sur ${total_orders_scanned} commande(s) scannee(s)`);
      } else if (skipped_existing > 0) {
        toast.info('Toutes les commissions sont deja a jour');
      } else {
        toast.info('Aucune commission a rattraper');
      }
      await fetchData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur lors du rattrapage'));
    } finally {
      setReconciling(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/commissions/my-dashboard`);
      setData(res.data);
    } catch {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Poll payment status if returning from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId || !isAuthenticated) return;

    let attempts = 0;
    const poll = async () => {
      if (attempts >= 5) return;
      attempts++;
      try {
        const res = await axios.get(`${API}/commissions/payment-status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          toast.success('Paiement effectue avec succes !');
          fetchData();
          return;
        }
      } catch {}
      setTimeout(poll, 2000);
    };
    poll();
  }, [searchParams, fetchData]);

  const handlePay = async (invoiceId) => {
    setPaying(invoiceId);
    try {
      const res = await axios.post(`${API}/commissions/pay-invoice/${invoiceId}`, {});
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur de paiement'));
      setPaying(null);
    }
  };

  const handleViewReceipt = async (invoiceId, existingUrl) => {
    if (existingUrl) {
      window.open(existingUrl, '_blank');
      return;
    }
    setFetchingReceipt(invoiceId);
    try {
      const res = await axios.get(`${API}/commissions/invoice-receipt/${invoiceId}`);
      if (res.data.receipt_url) {
        window.open(res.data.receipt_url, '_blank');
        fetchData();
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Recu non disponible'));
    } finally {
      setFetchingReceipt(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Commissions">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </DashboardLayout>
    );
  }

  const week = data?.current_week;
  const invoices = data?.invoices || [];

  return (
    <DashboardLayout title="Commissions">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Commissions & Facturation</h1>
            <p className="text-sm text-gray-500 mt-1">Suivi de vos commissions et factures LEMO</p>
          </div>
          <Button
            onClick={handleReconcile}
            disabled={reconciling}
            variant="outline"
            className="gap-2"
            data-testid="reconcile-commissions-btn"
          >
            {reconciling ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            {reconciling ? 'Rattrapage...' : 'Rattraper commissions manquantes'}
          </Button>
        </div>

        {/* Referral Credit Banner */}
        {data?.referral_credits_active && (
          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3" data-testid="referral-exempt-banner">
            <Gift className="w-5 h-5 text-emerald-600 shrink-0" />
            <p className="text-sm text-emerald-700 font-medium">
              Votre credit parrainage est actif — pas de commission ce mois-ci !
            </p>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card data-testid="stat-rate">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Taux de commission</CardTitle>
              <TrendingUp className="w-5 h-5 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{data?.commission_rate_pct || 5}%</div>
              <p className="text-xs text-gray-500 mt-1">Sur paiements au comptoir</p>
            </CardContent>
          </Card>

          <Card data-testid="stat-week-total">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Cette semaine</CardTitle>
              <Clock className="w-5 h-5 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600">{week?.total?.toFixed(2) || '0.00'} EUR</div>
              <p className="text-xs text-gray-500 mt-1">{week?.count || 0} commande(s)</p>
            </CardContent>
          </Card>

          <Card data-testid="stat-pending-invoices">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Factures en attente</CardTitle>
              <AlertCircle className="w-5 h-5 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {invoices.filter(i => i.status === 'pending').reduce((s, i) => s + i.total_amount, 0).toFixed(2)} EUR
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {invoices.filter(i => i.status === 'pending').length} facture(s)
              </p>
            </CardContent>
          </Card>

          <Card data-testid="stat-total-paid">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Total paye</CardTitle>
              <CheckCircle className="w-5 h-5 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-600">{data?.total_paid?.toFixed(2) || '0.00'} EUR</div>
              <p className="text-xs text-gray-500 mt-1">Depuis le debut</p>
            </CardContent>
          </Card>
        </div>

        {/* Current Week Detail */}
        {week?.commissions?.length > 0 && (
          <Card data-testid="current-week-detail">
            <CardHeader>
              <CardTitle className="text-base">Commissions de la semaine</CardTitle>
              <CardDescription>Du {new Date(week.week_start).toLocaleDateString('fr-FR')} a aujourd'hui</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {week.commissions.map((c) => (
                  <div key={c.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium">Commande #{c.order_id?.slice(-6)}</p>
                      <p className="text-xs text-gray-500">{new Date(c.created_at).toLocaleString('fr-FR')}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-amber-600">{c.commission_amount?.toFixed(2)} EUR</p>
                      <p className="text-xs text-gray-400">sur {c.order_amount?.toFixed(2)} EUR ({(c.commission_rate * 100).toFixed(0)}%)</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Invoices */}
        <Card data-testid="invoices-list">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              Historique des factures
            </CardTitle>
          </CardHeader>
          <CardContent>
            {invoices.length === 0 ? (
              <div className="text-center py-8">
                <Receipt className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">Aucune facture pour le moment</p>
                <p className="text-gray-400 text-xs mt-1">Les factures sont generees chaque lundi</p>
              </div>
            ) : (
              <div className="space-y-3">
                {invoices.map((inv) => (
                  <div key={inv.id} className={`flex items-center justify-between p-4 rounded-xl border ${
                    inv.status === 'paid' ? 'border-emerald-200 bg-emerald-50/50' :
                    'border-amber-200 bg-amber-50/50'
                  }`} data-testid={`invoice-${inv.id}`}>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-sm">Facture #{inv.id?.slice(-6).toUpperCase()}</p>
                        {inv.status === 'paid' ? (
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs">Payee</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700 text-xs">En attente</Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        Periode : {new Date(inv.period_start).toLocaleDateString('fr-FR')} - {new Date(inv.period_end).toLocaleDateString('fr-FR')}
                      </p>
                      <p className="text-xs text-gray-500">{inv.commission_count} commande(s)</p>
                    </div>
                    <div className="text-right flex items-center gap-3">
                      <div>
                        <p className="font-bold text-lg">{inv.total_amount?.toFixed(2)} EUR</p>
                        {inv.paid_at && (
                          <p className="text-xs text-emerald-600">Payee le {new Date(inv.paid_at).toLocaleDateString('fr-FR')}</p>
                        )}
                      </div>
                      {inv.status === 'pending' && (
                        <Button
                          size="sm"
                          onClick={() => handlePay(inv.id)}
                          disabled={paying === inv.id}
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid={`pay-invoice-${inv.id}`}
                        >
                          {paying === inv.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <CreditCard className="w-4 h-4 mr-1" />
                              Payer
                            </>
                          )}
                        </Button>
                      )}
                      {inv.status === 'paid' && (
                        <button
                          onClick={() => handleViewReceipt(inv.id, inv.stripe_receipt_url)}
                          disabled={fetchingReceipt === inv.id}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors disabled:opacity-50"
                          data-testid={`receipt-link-${inv.id}`}
                        >
                          {fetchingReceipt === inv.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <ExternalLink className="w-4 h-4" />
                          )}
                          Recu Stripe
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
